import React, { useState, useMemo } from 'react';
import { MapPin, Globe, Map, Navigation, Layers } from 'lucide-react';

/**
 * LocationVisualization Component
 * Displays location data from the OpenStreetMap geocoding response or manually entered data
 *
 * @param {Object} props
 * @param {Object} props.locationData - OpenStreetMap/Nominatim geocoding response or location data object
 * @param {Object} props.coordinates - { latitude, longitude } or { lat, lng }
 * @param {Object} props.addressData - Manual address entry { address, city, county, country, postalCode }
 * @param {string} props.view - Initial view: 'card', 'coordinates', or 'hierarchy'
 * @param {boolean} props.compact - Show compact version (single view, no toggle)
 * @param {string} props.className - Additional CSS classes
 */
const LocationVisualization = ({ 
  locationData, 
  coordinates, 
  addressData, 
  view: initialView = 'card',
  compact = false,
  className = ''
}) => {
  const [activeView, setActiveView] = useState(initialView);

  // Normalize location data from different sources
  const normalizedData = useMemo(() => {
    let data = {
      coordinates: null,
      address: '',
      city: '',
      county: '',
      country: '',
      sublocality: '',
      plusCode: '',
      placeId: '',
      bounds: null
    };

    // Handle OpenStreetMap geocoding response format
    if (locationData) {
      data.coordinates = locationData.coordinates || 
        (locationData.lat && locationData.lng ? { 
          latitude: locationData.lat, 
          longitude: locationData.lng 
        } : null);
      
      data.address = locationData.address || locationData.formatted_address || '';
      data.city = locationData.city || locationData.locality || '';
      data.county = locationData.county || locationData.administrative_area_level_1 || '';
      data.country = locationData.country || '';
      data.sublocality = locationData.sublocality || locationData.neighborhood || '';
      data.plusCode = locationData.plusCode || locationData.plus_code?.global_code || '';
      data.placeId = locationData.placeId || locationData.place_id || '';
      
      if (locationData.bounds) {
        data.bounds = locationData.bounds;
      }
    }

    // Handle separate coordinates prop
    if (coordinates) {
      data.coordinates = coordinates.latitude !== undefined 
        ? { latitude: coordinates.latitude, longitude: coordinates.longitude }
        : { latitude: coordinates.lat, longitude: coordinates.lng };
    }

    // Handle manual address entry
    if (addressData) {
      data.address = addressData.address || data.address || '';
      data.city = addressData.city || data.city || '';
      data.county = addressData.county || data.county || '';
      data.country = addressData.country || data.country || '';
      data.postalCode = addressData.postalCode || addressData.postal_code || '';
    }

    // Build formatted address if not provided
    if (!data.address && (data.city || data.county || data.country)) {
      const parts = [data.city, data.county, data.country].filter(Boolean);
      data.address = parts.join(', ') || '';
    }

    return data;
  }, [locationData, coordinates, addressData]);

  // Don't render if no data available
  if (!normalizedData.coordinates && !normalizedData.address && !normalizedData.city) {
    return null;
  }

  // View 1: Modern Location Card with Map Preview
  const LocationCard = () => (
    <div className={`bg-gradient-to-br from-accent-soft to-paper border border-line rounded-2xl p-6 shadow-xl ${compact ? 'p-4' : ''}`}>
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className={`font-bold text-ink mb-1 ${compact ? 'text-lg' : 'text-2xl'}`}>
            {compact ? 'Delivery Location' : 'Location Overview'}
          </h3>
          {!compact && <p className="text-ink-soft text-sm">Precise geolocation data</p>}
        </div>
        <div className="bg-accent p-2 rounded-full">
          <MapPin className="text-paper" size={compact ? 20 : 24} />
        </div>
      </div>

      {/* Interactive Map Preview */}
      {(normalizedData.coordinates || normalizedData.address) && (
        <div className="bg-surface rounded-xl p-4 mb-4 shadow-md relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
              <div className={`bg-accent rounded-full blur-3xl ${compact ? 'w-32 h-32' : 'w-48 h-48'}`}></div>
            </div>
          </div>

          <div className="relative">
            {normalizedData.coordinates && (
              <div className="flex items-center justify-center mb-3">
                <div className="relative">
                  <div className="absolute inset-0 bg-accent rounded-full animate-ping opacity-75"></div>
                  <div className="relative bg-accent rounded-full p-3">
                    <MapPin className="text-paper" size={compact ? 16 : 20} />
                  </div>
                </div>
              </div>
            )}

            <div className="text-center">
              <h4 className={`font-semibold text-ink mb-1 ${compact ? 'text-base' : 'text-lg'}`}>
                {normalizedData.address || normalizedData.city || 'Location'}
              </h4>
              {(normalizedData.city || normalizedData.county) && (
                <div className="flex items-center justify-center gap-2 text-ink-soft mb-3">
                  <Globe size={14} />
                  <span className="text-sm">
                    {[normalizedData.city, normalizedData.county].filter(Boolean).join(', ')}
                  </span>
                </div>
              )}
            </div>

            {normalizedData.coordinates && (
              <div className="grid grid-cols-2 gap-3 mt-4">
                <div className="bg-accent-soft rounded-lg p-3">
                  <p className="text-xs text-ink-soft mb-1">Latitude</p>
                  <p className="text-xs font-mono font-semibold text-ink">
                    {normalizedData.coordinates.latitude.toFixed(6)}°
                  </p>
                </div>
                <div className="bg-accent-soft rounded-lg p-3">
                  <p className="text-xs text-ink-soft mb-1">Longitude</p>
                  <p className="text-xs font-mono font-semibold text-ink">
                    {normalizedData.coordinates.longitude.toFixed(6)}°
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Address Hierarchy */}
      {(normalizedData.plusCode || normalizedData.sublocality || normalizedData.country) && (
        <div className="space-y-2">
          {[
            normalizedData.plusCode && { label: 'Plus Code', value: normalizedData.plusCode, icon: <Layers size={14} /> },
            normalizedData.sublocality && { label: 'Sublocality', value: normalizedData.sublocality, icon: <Map size={14} /> },
            normalizedData.country && { label: 'Country', value: normalizedData.country, icon: <Globe size={14} /> }
          ].filter(Boolean).map((item, idx) => (
            <div key={idx} className="flex items-center justify-between bg-surface rounded-lg p-3 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-2">
                <div className="text-accent">{item.icon}</div>
                <span className="text-xs text-ink-soft">{item.label}</span>
              </div>
              <span className="text-xs font-semibold text-ink">{item.value}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // View 2: Coordinate Grid System
  const CoordinateGrid = () => {
    if (!normalizedData.coordinates) return null;

    return (
      <div className="bg-ink rounded-2xl p-6 shadow-2xl text-paper">
        <div className="flex items-center gap-3 mb-6">
          <Navigation className="text-brass" size={28} />
          <h3 className="text-2xl font-bold">Geographic Coordinates</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {/* Latitude Panel */}
          <div className="bg-gradient-to-br from-accent to-accent-deep rounded-xl p-5 shadow-lg">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-2 h-2 bg-paper rounded-full animate-pulse"></div>
              <p className="text-xs font-semibold uppercase tracking-wider">Latitude</p>
            </div>
            <div className="bg-paper/15 rounded-lg p-3 backdrop-blur-sm">
              <p className="text-3xl font-bold font-mono mb-1">
                {normalizedData.coordinates.latitude.toFixed(4)}°
              </p>
              <p className="text-xs opacity-90">
                {normalizedData.coordinates.latitude < 0 ? 'South of Equator' : 'North of Equator'}
              </p>
            </div>
            {normalizedData.bounds && (
              <div className="mt-3 text-xs opacity-75">
                <p>Precision: ±{((normalizedData.bounds.northeast.lat - normalizedData.bounds.southwest.lat) * 111000).toFixed(1)}m</p>
              </div>
            )}
          </div>

          {/* Longitude Panel */}
          <div className="bg-gradient-to-br from-brass to-[#8C6E2E] rounded-xl p-5 shadow-lg">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-2 h-2 bg-paper rounded-full animate-pulse"></div>
              <p className="text-xs font-semibold uppercase tracking-wider">Longitude</p>
            </div>
            <div className="bg-paper/15 rounded-lg p-3 backdrop-blur-sm">
              <p className="text-3xl font-bold font-mono mb-1">
                {normalizedData.coordinates.longitude.toFixed(4)}°
              </p>
              <p className="text-xs opacity-90">
                {normalizedData.coordinates.longitude < 0 ? 'West of Prime Meridian' : 'East of Prime Meridian'}
              </p>
            </div>
            {normalizedData.bounds && (
              <div className="mt-3 text-xs opacity-75">
                <p>Precision: ±{((normalizedData.bounds.northeast.lng - normalizedData.bounds.southwest.lng) * 111000 * Math.cos(normalizedData.coordinates.latitude * Math.PI / 180)).toFixed(1)}m</p>
              </div>
            )}
          </div>
        </div>

        {/* Bounds Visualization */}
        {normalizedData.bounds && (
          <div className="bg-ink-soft/40 rounded-xl p-5">
            <h4 className="text-base font-semibold mb-3 flex items-center gap-2">
              <Layers size={18} className="text-brass" />
              Bounding Box
            </h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-paper/10 rounded-lg p-3">
                <p className="text-xs text-paper/60 mb-1">Northeast Corner</p>
                <p className="font-mono text-xs">{normalizedData.bounds.northeast.lat.toFixed(6)}</p>
                <p className="font-mono text-xs">{normalizedData.bounds.northeast.lng.toFixed(6)}</p>
              </div>
              <div className="bg-paper/10 rounded-lg p-3">
                <p className="text-xs text-paper/60 mb-1">Southwest Corner</p>
                <p className="font-mono text-xs">{normalizedData.bounds.southwest.lat.toFixed(6)}</p>
                <p className="font-mono text-xs">{normalizedData.bounds.southwest.lng.toFixed(6)}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // View 3: Hierarchical Address Breakdown
  const AddressHierarchy = () => {
    const hierarchyItems = [
      normalizedData.plusCode && { level: 'Plus Code', value: normalizedData.plusCode, color: 'accent', size: 'large' },
      normalizedData.address && { level: 'Street Address', value: normalizedData.address, color: 'accent', size: 'large' },
      normalizedData.sublocality && { level: 'Sublocality', value: normalizedData.sublocality, color: 'brass', size: 'medium' },
      normalizedData.city && { level: 'City', value: normalizedData.city, color: 'brass', size: 'medium' },
      normalizedData.county && { level: 'County', value: normalizedData.county, color: 'ink', size: 'small' },
      normalizedData.country && { level: 'Country', value: normalizedData.country, color: 'ink', size: 'small' }
    ].filter(Boolean);

    if (hierarchyItems.length === 0) return null;

    return (
      <div className="bg-surface rounded-2xl p-6 shadow-xl">
        <div className="text-center mb-6">
          <h3 className="text-2xl font-bold text-ink mb-1">Address Hierarchy</h3>
          <p className="text-ink-soft text-sm">From specific to general</p>
        </div>

        <div className="relative">
          {/* Connecting Line */}
          <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-accent via-brass to-ink-soft"></div>

          {/* Hierarchy Items */}
        <div className="space-y-4">
          {hierarchyItems.map((item, idx) => {
            // Map color to Tailwind classes
            const colorClasses = {
              accent: {
                bg: 'bg-accent',
                gradient: 'from-accent-soft'
              },
              brass: {
                bg: 'bg-brass',
                gradient: 'from-brass/10'
              },
              ink: {
                bg: 'bg-ink-soft',
                gradient: 'from-paper'
              }
            };
            const colorClass = colorClasses[item.color] || colorClasses.brass;

            return (
              <div key={idx} className="relative flex items-center gap-3">
                {/* Node */}
                <div className={`relative z-10 w-12 h-12 ${colorClass.bg} rounded-full flex items-center justify-center shadow-lg`}>
                  <div className="w-6 h-6 bg-surface rounded-full flex items-center justify-center">
                    <span className="text-xs font-bold text-ink">{idx + 1}</span>
                  </div>
                </div>

                {/* Content Card */}
                <div className={`flex-1 bg-gradient-to-r ${colorClass.gradient} to-surface rounded-xl p-3 shadow-md hover:shadow-lg transition-shadow`}>
                  <p className="text-xs font-semibold text-ink-faint uppercase tracking-wider mb-1">
                    {item.level}
                  </p>
                  <p className={`font-semibold text-ink ${item.size === 'large' ? 'text-base' : item.size === 'medium' ? 'text-sm' : 'text-xs'}`}>
                    {item.value}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
        </div>

        {/* Metadata Footer */}
        {normalizedData.placeId && (
          <div className="mt-6 bg-paper rounded-xl p-4">
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <p className="text-ink-soft mb-1">Place ID</p>
                <p className="font-mono text-xs text-ink break-all">{normalizedData.placeId}</p>
              </div>
              <div>
                <p className="text-ink-soft mb-1">Data Source</p>
                <p className="font-semibold text-ink">OpenStreetMap</p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Compact mode: show only the card view
  if (compact) {
    return (
      <div className={className}>
        <LocationCard />
      </div>
    );
  }

  // Full mode: show view selector and all views
  return (
    <div className={className}>
      {/* View Selector */}
      <div className="mb-6">
        <div className="flex gap-2 bg-surface rounded-xl p-2 shadow-lg">
          {[
            { id: 'card', label: 'Overview', icon: <MapPin size={16} /> },
            { id: 'coordinates', label: 'Coordinates', icon: <Navigation size={16} /> },
            { id: 'hierarchy', label: 'Hierarchy', icon: <Layers size={16} /> }
          ].map((view) => (
            <button
              key={view.id}
              onClick={() => setActiveView(view.id)}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-semibold transition-colors text-sm ${
                activeView === view.id
                  ? 'bg-accent text-paper shadow-md'
                  : 'text-ink-soft hover:bg-paper'
              }`}
            >
              {view.icon}
              <span className="hidden sm:inline">{view.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Active View */}
      <div>
        {activeView === 'card' && <LocationCard />}
        {activeView === 'coordinates' && <CoordinateGrid />}
        {activeView === 'hierarchy' && <AddressHierarchy />}
      </div>
    </div>
  );
};

export default LocationVisualization;

